import React, { useState } from 'react'
import { ChevronDown, User, Clock, CreditCard, Shield, Zap, Heart, Plus } from 'lucide-react'
import { track } from '../../lib/analytics/posthog'

interface FAQItem {
  question: string
  answer: string
  icon: React.ReactNode
}

const faqItems: FAQItem[] = [
  {
    question: "Who's behind this?",
    answer: "Hi! I'm Stan, a solo developer who built this tool. I'm not some faceless corporation - just a guy who loves building useful products. I originally created the podcast generator for my own projects and decided to share it when others found it helpful. You can find me on social media @stanvanrooy if you want to say hi!",
    icon: <User className="h-5 w-5" />
  },
  {
    question: "What if I don't like the tool?",
    answer: "I offer a 7-day money-back guarantee. If you're not satisfied within the first week, just let me know and I'll issue a full refund. After that, you can cancel your subscription at any time, and you'll keep access until your current billing period ends.",
    icon: <Shield className="h-5 w-5" />
  },
  {
    question: "How quickly can I generate podcasts?",
    answer: "Most podcasts are ready in 2-5 minutes, depending on length and complexity. The tool is designed to be fast because I know you don't want to wait around. I've optimized everything to give you quality results quickly so you can focus on creating content, not waiting for it.",
    icon: <Zap className="h-5 w-5" />
  },
  {
    question: "Will my subscription auto-renew?",
    answer: "Yes, but you're always in control. You can cancel anytime from this page - no need to email me or jump through hoops. I believe in making things simple. If you cancel, you'll get a confirmation and your subscription will end after your current period.",
    icon: <CreditCard className="h-5 w-5" />
  },
  {
    question: "What happens if I hit my limit?",
    answer: "The tool will let you know when you're getting close to your monthly limit. If you hit it, you can either buy a one-time top-up (50, 100, or 250 minutes), upgrade to a higher plan, or wait until next month when your plan credits reset. Top-up minutes never expire and are used first!",
    icon: <Clock className="h-5 w-5" />
  },
  {
    question: "How do minute top-ups work?",
    answer: "Top-ups let you buy extra minutes instantly without changing your plan. The price per minute matches your current plan rate - higher plans get better rates! These bonus minutes never expire and are always used before your monthly plan credits. Perfect for busy months or one-off projects.",
    icon: <Plus className="h-5 w-5" />
  },
  {
    question: "Why should I trust this tool?",
    answer: "I've been building developer tools for years (my other project, Compress GLB, is used by thousands). I'm committed to making this the best podcast generator available. I use it myself, I listen to user feedback, and I'm constantly improving it. Plus, you can see real user testimonials - these aren't fake reviews.",
    icon: <Heart className="h-5 w-5" />
  }
]

interface FAQItemProps {
  item: FAQItem
  isOpen: boolean
  onToggle: () => void
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border border-black/10 rounded-xl bg-white overflow-hidden transition-all duration-200 hover:shadow-sm">
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-black/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-brand-600 flex-shrink-0">
            {item.icon}
          </div>
          <span className="font-medium text-foreground">{item.question}</span>
        </div>
        <ChevronDown 
          className={`h-5 w-5 text-foreground/50 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-4 pl-13">
          <p className="text-sm text-foreground/70 leading-relaxed">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  )
}

const BillingFAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set())

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems)
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index)
    } else {
      // Track when a user actively seeks an answer
      track('faq_item_expanded', {
        question: faqItems[index].question,
        index: index,
        group: 'billing_faq'
      })
      newOpenItems.add(index)
    }
    setOpenItems(newOpenItems)
  }

  return (
    <section aria-labelledby="faq-heading" className="space-y-4">
      <div className="text-center space-y-2">
        <h2 id="faq-heading" className="text-lg font-semibold text-foreground">
          Questions? I've got answers
        </h2>
        <p className="text-sm text-foreground/60 max-w-lg mx-auto">
          Common questions about the tool, billing, and what you can expect. 
          If you don't see your question here, feel free to reach out!
        </p>
      </div>
      
      <div className="space-y-3">
        {faqItems.map((item, index) => (
          <FAQItemComponent
            key={index}
            item={item}
            isOpen={openItems.has(index)}
            onToggle={() => toggleItem(index)}
          />
        ))}
      </div>
      
      <div className="text-center pt-4">
        <p className="text-xs text-foreground/50">
          Still have questions? Reach out on{' '}
          <a 
            href="https://twitter.com/stanvanrooy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-brand-600 hover:text-brand-700 underline decoration-brand-300 underline-offset-2 cursor-pointer"
          >
            Twitter
          </a>
          {' '}or{' '}
          <a 
            href="mailto:stan@podcast-generator.ai" 
            className="text-brand-600 hover:text-brand-700 underline decoration-brand-300 underline-offset-2 cursor-pointer"
          >
            email
          </a>
        </p>
      </div>
    </section>
  )
}

export default BillingFAQ